import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import CheckboxInput from 'components/form/CheckboxInput.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import POS_READER_STATUS from 'constants/pos/posReaderStatus.const';
import useResettingState from 'hooks/useResettingState.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectUserStylistId } from 'selectors/simple';
import alen from 'utils/arrays/alen.util';
import { firstOf } from 'utils/arrays/elements.util';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import call from 'utils/functions/call.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import { isValidId } from 'utils/predicates/fields.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

import CN from './CardReadersModal.module.scss';

const READER_STATUS = Object.freeze({
  online: POS_READER_STATUS.online,
  offline: POS_READER_STATUS.offline,
  ordered: POS_READER_STATUS.ordered,
});

const sortById = (a, b) => {
  const aa = a?.id;
  const bb = b?.id;

  return aa < bb ? -1 : aa > bb ? 1 : 0;
};

const CardReadersModal = ({ isOpen, onClose, onSelect, isLoading, readers, generalReaderStatus, stylistId, tag }) => {
  const { t } = useTranslation();

  const sortedReaders = [...noNilArray(readers)].sort(sortById);

  const onlineReaders = sortedReaders.filter((reader) => READER_STATUS.online === reader.status);
  const hasNoOnlineReader = !onlineReaders.length;

  const hasValidUserStylistId = isValidId(stylistId);

  const [value, setValue] = useResettingState(
    onlineReaders.find((r) => hasValidUserStylistId && isValidId(r?.stylistId) && r?.stylistId === stylistId) ?? onlineReaders.find((r) => r.isMain) ?? firstOf(onlineReaders),
  );

  const handleNext = useCallback(() => void call('CardReadersModal.onSelect', onSelect, { value }), [onSelect, value]);
  const handleClose = useCallback(() => void call('CardReadersModal.onClose', onClose, { value: null }), [onClose]);

  return (
    <SalonModal tag={ct('CardReadersModal', tag)} isOpen={isOpen} withFooter banner="backgroundImageWave">
      <SalonModal.Header>
        <div className={CN.modalHeader}>
          <div className={CN.modalHeaderTitle}>{t('composite.cardReaderModal.title')}</div>

          <div className={CN.closeModalButton}>
            <Button tag="CardReadersModal__close" variant="empty" onClick={handleClose}>
              <Icon variant="close" />
            </Button>
          </div>
        </div>
      </SalonModal.Header>
      <SalonModal.Body>
        <div data-bem="CardReadersModal" className={CN.component} data-id={value?.id} data-reader-id={value?.readerId} data-reader-name={value?.name}>
          {isLoading || !alen(readers) ? (
            <OverlaySpinner />
          ) : (
            <div data-bem="CardReadersModal__container" className={CN.container}>
              <div className={CN.title}>{t('composite.cardReaderModal.chooseCardReader')}</div>

              {hasNoOnlineReader && (
                <div className={CN.alertOuterContainer}>
                  <AlertPanel tag="CardReadersModal__alert" variant="error">
                    <div className={CN.alertInnerContainer}>
                      <Icon variant="redSiren" width="md" />
                      <div>
                        <div className={CN.alertTitle}> {t('composite.cardReaderModal.offlineReaders')}</div>
                        {t('composite.cardReaderModal.ensureMessage')}
                      </div>
                    </div>
                  </AlertPanel>
                </div>
              )}

              <div className={CN.readersList}>
                {sortedReaders.map((candidate) => {
                  const { stylist, name, id, status, readerId } = candidate ?? {};

                  const isChecked = value === candidate;
                  const isDisabled = READER_STATUS.online !== status;

                  // click on the div should not work if the reader is not online, so set the handler accordingly
                  const handleClick = isDisabled ? alwaysNoop : () => setValue(candidate);

                  return (
                    <div
                      data-bem="CardReadersModal__reader"
                      data-id={id}
                      data-reader-id={readerId}
                      data-reader-name={name}
                      data-reader-status={status}
                      key={id}
                      className={cn({
                        [CN.readerContainer]: true,
                        [CN.isSelected]: isChecked,
                        [CN.isDisabled]: isDisabled,
                      })}
                      onClick={handleClick}
                    >
                      <div className={CN.readerItem}>
                        <div className={CN.leftGroup}>
                          <CheckboxInput key={id} variant="option" checked={isChecked} disabled={isDisabled} />

                          <div className={CN.readerName}>
                            {name || '---'}
                            {fromAnyToBool(stylist) && <span className={CN.readerStylist}>({stylist})</span>}
                          </div>
                        </div>

                        <Badge variant={status === READER_STATUS.online ? 'success' : 'restricted'}>
                          <span className={CN.readerStatus}>
                            <Icon variant={status === READER_STATUS.online ? 'checkCircleSolid' : 'cloudOff'} width="md" />
                            {status}
                          </span>
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        <Button tag="CardReadersModal__button-next" variant="primary" disabled={!value} onClick={handleNext}>
          {t('common.buttons.next')}
        </Button>
      </SalonModal.Footer>
    </SalonModal>
  );
};

const CardReadersModalHoc = connect((state) => ({
  stylistId: selectUserStylistId(state),
}))(CardReadersModal);

export default CardReadersModalHoc;
